import PropTypes from 'prop-types';
import React from 'react';
import { Field } from 'redux-form';
import { scroller } from 'react-scroll';
import { PercentageField } from '../../components/form/field';
import Checkbox from '../form/Checkbox';
import CheckboxQuestion from '../form/CheckboxQuestion';
import Form from './Form';
import formProvider from './formProvider';
import FormButtons from '../FormButtons';
import RadioQuestion from './RadioQuestion';
import { GridLayout } from '../form/layout';
import Title from '../Title';
import { setModal } from '../../actions';
import SignatureWarningModal from './SignatureWarningModal';
import './Survey.scss';

import {
    financialKnowledge,
    legalKnowledge,
    investmentKnowledge,
    investmentPossession,
    possibilityOfSouscription,
    yesNoConductFinancialTransactionsWithoutAdvisor,
    yesNoConductFinancialTransactionsWithAdvisor,
    yesNoDelegatingPortfolio,
    doneInvestmentTypes,
    investmentInFinancialSupport,
    yesNoTradingLossKnowledge,
    downwardPrediction,
    yesNoSufferTradingLosses,
    doneReactionOnLoss,
    supportLoss,
    investmentStress,
    availableSavings,
    investmentTimeHorizon,
    percentageInvestment,
    investmentDescription,
    favoriteEvolution,
    lifeInsuranceSouscription,
    lifeInsuranceDown,
    yesNoLifeInsuranceLosses,
    lifeInsuranceCauses,
    diferenceFondEurosAndUnity,
    drivenManagement,
    drivenFree,
    discretionaryManagement,
    investmentPeriod,
    objectBeneficiaryClause,
    clauseOrModels,
    // investissement durable
    yesNoDurableInvestment,
    advisorTakesEnviromentalCriteria,
    choicesEnvironmentalCriteria,
    advisorTakeSocialCriteria,
    choicesSocialCriteria,
    advisorTakeGovernanceCriteria,
    choicesGovernanceCriteria,
    lowerPerformanceForESGInvestments,
    isPoliticallyExposedOptions,
} from '../../common/choices/investorProfile';

const scrollTo = (field) => () => scroller.scrollTo(`scroll.${field}`, {
    smooth: true,
    containerId: 'main',
    offset: -110,
});

const SurveyForm = ({
    prefix,
    decline,
    getFieldName,
    sufferTradingLosses,
    lifeInsuranceLosses,
    durableInvestment,
    enviromentalCriteria,
    socialCriteria,
    governanceCriteria,
    declinePartner,
    sufferTradingLossesPartner,
    lifeInsuranceLossesPartner,
    durableInvestmentPartner,
    enviromentalCriteriaPartner,
    socialCriteriaPartner,
    governanceCriteriaPartner,
}) => {
    const declineSelected = 'client' === prefix ? decline : declinePartner;
    const sufferTradingLossesSelected = 'client' === prefix ? sufferTradingLosses : sufferTradingLossesPartner;
    const lifeInsuranceLossesSelected = 'client' === prefix ? lifeInsuranceLosses : lifeInsuranceLossesPartner;
    const durableInvestmentSelected = 'client' === prefix ? durableInvestment : durableInvestmentPartner;
    const enviromentalCriteriaSelected = 'client' === prefix ? enviromentalCriteria : enviromentalCriteriaPartner;
    const socialCriteriaSelected = 'client' === prefix ? socialCriteria : socialCriteriaPartner;
    const governanceCriteriaSelected = 'client' === prefix ? governanceCriteria : governanceCriteriaPartner;

    return (
        <div className="survey-form">
            <Field
                title="Je ne souhaite pas répondre à ce questionnaire de connaissances règlementaire. Mon conseiller ne pourra donc réaliser aucune préconisation."
                component={Checkbox}
                name={getFieldName(prefix, 'decline')}
            />
            {!declineSelected ? (
                <div>
                    <p className="bold">Connaissance et expérience</p>

                    <RadioQuestion
                        choices={financialKnowledge}
                        name={getFieldName(prefix, 'financialKnowledge')}
                        title="Comment considérez-vous votre connaissance financière&nbsp;?"
                        className="triple"
                        onChange={scrollTo(getFieldName(prefix, 'legalKnowledge'))}
                    />
                    <RadioQuestion
                        choices={legalKnowledge}
                        name={getFieldName(prefix, 'legalKnowledge')}
                        title="Comment considérez-vous votre connaissance juridique&nbsp;?"
                        className="triple"
                        onChange={scrollTo(getFieldName(prefix, 'investmentKnowledge'))}
                    />
                    <CheckboxQuestion
                        choices={investmentKnowledge}
                        name={getFieldName(prefix, 'investmentKnowledge')}
                        title="Quels sont les supports de placement que vous connaissez&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'investmentPossession'))}
                    />
                    <CheckboxQuestion
                        choices={investmentPossession}
                        name={getFieldName(prefix, 'investmentPossession')}
                        title="Quels sont les supports de placement que vous possédez&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'possibilityOfSouscription'))}
                    />
                    <RadioQuestion
                        choices={possibilityOfSouscription}
                        name={getFieldName(prefix, 'possibilityOfSouscription')}
                        title="De ces 3 placements, lequel pourriez-vous souscrire&nbsp;?"
                        className="single"
                        onChange={scrollTo(getFieldName(prefix, 'yesNoConductFinancialTransactionsWithoutAdvisor'))}
                    />
                    <RadioQuestion
                        choices={yesNoConductFinancialTransactionsWithoutAdvisor}
                        name={getFieldName(prefix, 'yesNoConductFinancialTransactionsWithoutAdvisor')}
                        title={
                            <span>Avez-vous déjà réalisé des opérations financières <b>sans</b> conseiller financier&nbsp;?</span>}
                        helpText="Ex. compte en ligne, bourse en ligne …"
                        onChange={scrollTo(getFieldName(prefix, 'yesNoConductFinancialTransactionsWithAdvisor'))}
                    />
                    <RadioQuestion
                        choices={yesNoConductFinancialTransactionsWithAdvisor}
                        name={getFieldName(prefix, 'yesNoConductFinancialTransactionsWithAdvisor')}
                        title={<span>Avez-vous déjà réalisé des opérations financières <b>avec</b> un conseiller financier&nbsp;?</span>}
                        helpText="Ex. avec votre banquier ou un CGP …"
                        onChange={scrollTo(getFieldName(prefix, 'yesNoDelegatingPortfolio'))}
                    />
                    <RadioQuestion
                        choices={yesNoDelegatingPortfolio}
                        name={getFieldName(prefix, 'yesNoDelegatingPortfolio')}
                        title="En cas de réalisation d'investissement financiers, déléguez-vous la gestion de tout ou partie de votre portefeuille de valeurs mobilières&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'doneInvestmentTypes'))}
                    />
                    <CheckboxQuestion
                        choices={doneInvestmentTypes}
                        name={getFieldName(prefix, 'doneInvestmentTypes')}
                        title="Sur quels types de placements avez-vous déjà investi&nbsp;?"
                        className="single"
                        onChange={scrollTo(getFieldName(prefix, 'investmentInFinancialSupport'))}
                    />
                    <CheckboxQuestion
                        choices={investmentInFinancialSupport}
                        name={getFieldName(prefix, 'investmentInFinancialSupport')}
                        title="Avez-vous déjà investi dans les supports financiers suivants&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'yesNoTradingLossKnowledge'))}
                    />
                    <RadioQuestion
                        choices={yesNoTradingLossKnowledge}
                        name={getFieldName(prefix, 'yesNoTradingLossKnowledge')}
                        title="Les placements réalisés sur les actions, obligations ou fonds commun de placement, peuvent-ils subir une perte en capital&nbsp;? *"
                        className="triple"
                        onChange={scrollTo(getFieldName(prefix, 'downwardPrediction'))}
                    />
                    <p className="text-muted margin-bottom-form wording">
                        * Les placements réalisés sur les actions, obligations ou fonds commun de placement, peuvent subir une perte en capital.
                    </p>
                    <RadioQuestion
                        choices={downwardPrediction}
                        name={getFieldName(prefix, 'downwardPrediction')}
                        title="Les marchés financiers peuvent monter ou baisser, si dans les 5 ans votre placement baissait, quelle serait votre réaction&nbsp;?"
                        className="single"
                        onChange={scrollTo(getFieldName(prefix, 'sufferTradingLosses'))}
                    />
                    <RadioQuestion
                        choices={yesNoSufferTradingLosses}
                        name={getFieldName(prefix, 'sufferTradingLosses')}
                        title="Avez-vous déjà subi des pertes en investissant sur des produits financiers hors assurance vie&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'yes' === sufferTradingLossesSelected
                            ? 'doneReactionOnLoss'
                            : 'investmentStress'))}
                    />
                    {'yes' === sufferTradingLossesSelected ? (
                        <RadioQuestion
                            choices={doneReactionOnLoss}
                            name={getFieldName(prefix, 'doneReactionOnLoss')}
                            title="Quelle a été votre réaction en cas de perte d'argent&nbsp;?"
                            onChange={scrollTo(getFieldName(prefix, 'supportLoss'))}
                        />
                    ) : null}
                    {'yes' === sufferTradingLossesSelected ? (
                        <RadioQuestion
                            choices={supportLoss}
                            name={getFieldName(prefix, 'supportLoss')}
                            title="Sur quels supports avez-vous subi des pertes&nbsp;?"
                            onChange={scrollTo(getFieldName(prefix, 'investmentStress'))}
                        />
                    ) : null}
                    <RadioQuestion
                        choices={investmentStress}
                        name={getFieldName(prefix, 'investmentStress')}
                        title="Êtes-vous stressé par vos investissements financiers&nbsp;?"
                        className="single"
                        onChange={scrollTo(getFieldName(prefix, 'availableSavings'))}
                    />
                    <p className="bold">
                        Souhait d&apos;investissement
                    </p>
                    <RadioQuestion
                        choices={availableSavings}
                        name={getFieldName(prefix, 'availableSavings')}
                        title="Pensez vous avoir besoin de la somme investie pendant la durée de placement envisagée&nbsp;?"
                        className="triple"
                        onChange={scrollTo(getFieldName(prefix, 'investmentTimeHorizon'))}
                    />
                    <RadioQuestion
                        choices={investmentTimeHorizon}
                        name={getFieldName(prefix, 'investmentTimeHorizon')}
                        title="Quel est votre horizon de placement&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'percentageInvestment'))}
                    />
                    <RadioQuestion
                        choices={percentageInvestment}
                        name={getFieldName(prefix, 'percentageInvestment')}
                        title="Quel est le pourcentage de votre épargne disponible que vous souhaitez attribuer à ce placement&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'investmentDescription'))}
                    />
                    <RadioQuestion
                        choices={investmentDescription}
                        name={getFieldName(prefix, 'investmentDescription')}
                        title="Quelle phrase correspond au mieux à votre souhait d'investissement&nbsp;?"
                        className="single"
                        onChange={scrollTo(getFieldName(prefix, 'favoriteEvolution'))}
                    />
                    <RadioQuestion
                        choices={favoriteEvolution}
                        name={getFieldName(prefix, 'favoriteEvolution')}
                        title="Quelle évolution préférez-vous pour votre capital&nbsp;?"
                        className="single"
                        onChange={scrollTo(getFieldName(prefix, 'yesNoDurableInvestment'))}
                    />
                    <RadioQuestion
                        choices={yesNoDurableInvestment}
                        name={getFieldName(prefix, 'yesNoDurableInvestment')}
                        title="Etes-vous susceptible d’être intéressé par l’investissement durable&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'yes' === yesNoDurableInvestment
                            ? 'advisorTakesEnviromentalCriteria'
                            : 'yesNoDurableInvestment'))}
                    />

                    {'yes' === durableInvestmentSelected ? (
                        <div>
                            <PercentageField
                                title={<span> Dans quelle proportion souhaitez-vous prendre en compte des critères de placement durable, sociaux et environnementaux pour votre investissement (%): </span>}
                                name={getFieldName(prefix, 'investmentRatio')}
                                min={0}
                                max={100}
                                isRequired
                            />

                            <RadioQuestion
                                choices={advisorTakesEnviromentalCriteria}
                                name={getFieldName(prefix, 'advisorTakesEnviromentalCriteria')}
                                title="Souhaitez-vous que votre conseiller prenne en compte des critères Environnementaux&nbsp;?"
                                onChange={scrollTo(getFieldName(prefix, 'yes' === advisorTakesEnviromentalCriteria
                                    ? 'choicesEnvironmentalCriteria'
                                    : 'advisorTakeSocialCriteria'))}
                            />
                            { 'yes' === enviromentalCriteriaSelected ? (
                                <CheckboxQuestion
                                    choices={choicesEnvironmentalCriteria}
                                    name={getFieldName(prefix, 'choicesEnvironmentalCriteria')}
                                    title="Lesquels&nbsp;?"
                                />

                            ) : null }
                            <RadioQuestion
                                choices={advisorTakeSocialCriteria}
                                name={getFieldName(prefix, 'advisorTakeSocialCriteria')}
                                title="Souhaitez-vous que votre conseiller prenne en compte des critères Sociaux&nbsp;?"
                                onChange={scrollTo(getFieldName(prefix, 'yes' === advisorTakeSocialCriteria
                                    ? 'choicesSocialCriteria'
                                    : 'advisorTakeGovernanceCriteria'))}
                            />
                            {'yes' === socialCriteriaSelected && (
                                <CheckboxQuestion
                                    choices={choicesSocialCriteria}
                                    name={getFieldName(prefix, 'choicesSocialCriteria')}
                                    title="Lesquels&nbsp;?"
                                    className="single"
                                />
                            )}
                            <RadioQuestion
                                choices={advisorTakeGovernanceCriteria}
                                name={getFieldName(prefix, 'advisorTakeGovernanceCriteria')}
                                title="Souhaitez-vous que votre conseiller prenne en compte des critères de Gouvernance&nbsp;?"
                                onChange={scrollTo(getFieldName(prefix, 'yes' === advisorTakeGovernanceCriteria
                                    ? 'choicesGovernanceCriteria'
                                    : 'lowerPerformanceForESGInvestments'))}
                            />
                            {'yes' === governanceCriteriaSelected && (
                                <CheckboxQuestion
                                    choices={choicesGovernanceCriteria}
                                    name={getFieldName(prefix, 'choicesGovernanceCriteria')}
                                    title="Lesquels&nbsp;?"
                                />
                            )}
                            <RadioQuestion
                                choices={lowerPerformanceForESGInvestments}
                                name={getFieldName(prefix, 'lowerPerformanceForESGInvestments')}
                                title="Êtes-vous prêt à accepter des performances moindres en cas d’investissements répondant à des critères d’ESG &nbsp;?"
                            />
                        </div>
                    ) : null }

                    <p className="bold">L&apos;assurance vie</p>
                    <RadioQuestion
                        choices={lifeInsuranceSouscription}
                        name={getFieldName(prefix, 'lifeInsuranceSouscription')}
                        title="Avez-vous déjà souscrit un contrat d'assurance-vie&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'lifeInsuranceDown'))}
                    />
                    <RadioQuestion
                        choices={lifeInsuranceDown}
                        name={getFieldName(prefix, 'lifeInsuranceDown')}
                        title="Dans l'hypothèse où le capital de contrat d'assurance vie est à la baisse&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'lifeInsuranceLosses'))}
                    />
                    <RadioQuestion
                        choices={yesNoLifeInsuranceLosses}
                        name={getFieldName(prefix, 'lifeInsuranceLosses')}
                        title="Avez-vous subi des pertes sur votre contrat d'assurance vie&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'yes' === lifeInsuranceLossesSelected
                            ? 'lifeInsuranceCauses'
                            : 'diferenceFondEurosAndUnity'))}
                    />
                    {'yes' === lifeInsuranceLossesSelected && (
                        <RadioQuestion
                            choices={lifeInsuranceCauses}
                            name={getFieldName(prefix, 'lifeInsuranceCauses')}
                            title="Si oui, pour quelle raison&nbsp;?"
                            onChange={scrollTo(getFieldName(prefix, 'diferenceFondEurosAndUnity'))}
                        />
                    )}
                    <RadioQuestion
                        choices={diferenceFondEurosAndUnity}
                        name={getFieldName(prefix, 'diferenceFondEurosAndUnity')}
                        title="Connaissez vous la différence entre les fonds en euros et les fonds en unités de compte dans un contrat d’assurance vie&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'drivenManagement'))}
                    />
                    <RadioQuestion
                        choices={drivenManagement}
                        name={getFieldName(prefix, 'drivenManagement')}
                        title="Connaissez vous les modalités d'une gestion pilotée&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'drivenFree'))}
                    />
                    <RadioQuestion
                        choices={drivenFree}
                        name={getFieldName(prefix, 'drivenFree')}
                        title="Connaissez vous les modalités d'une gestion libre&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'discretionaryManagement'))}
                    />
                    <RadioQuestion
                        choices={discretionaryManagement}
                        name={getFieldName(prefix, 'discretionaryManagement')}
                        title="Connaissez vous les modalités d'une gestion sous mandat&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'investmentPeriod'))}
                    />
                    <RadioQuestion
                        choices={investmentPeriod}
                        name={getFieldName(prefix, 'investmentPeriod')}
                        title="Sur quelle durée souhaitez vous réaliser ce type de placement&nbsp;?"
                        className="triple"
                        onChange={scrollTo(getFieldName(prefix, 'objectBeneficiaryClause'))}
                    />
                    <RadioQuestion
                        choices={objectBeneficiaryClause}
                        name={getFieldName(prefix, 'objectBeneficiaryClause')}
                        title="Savez vous quel est l'objet de la clause bénéficiaire&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'clauseOrModels'))}
                    />
                    <RadioQuestion
                        choices={clauseOrModels}
                        name={getFieldName(prefix, 'clauseOrModels')}
                        title="Souhaitez vous rédiger la clause bénéficiaire ou utiliser les modèles proposés&nbsp;?"
                        onChange={scrollTo(getFieldName(prefix, 'clauseOrModels'))}
                    />
                    <RadioQuestion
                        choices={isPoliticallyExposedOptions}
                        name={getFieldName(prefix, 'isPoliticallyExposedOptions')}
                        title="Etes vous considéré comme une Personne Politiquement Exposée (une personne qui a occupé ou occupe une fonction publique importante au sein d’un gouvernement, d’une organisation nationale ou internationale ou encore d’une administration importante. Les PPE, du fait de leur statut et de leur influence, sont davantage exposées à des activités financières sensibles et leur implication peut engendrer un risque plus élevé en matière de blanchiment de capitaux, financement de terrorisme ou corruption)&nbsp;?"
                    />
                    <p className="bold">
                        En l&apos;absence de réponse à ce questionnaire, mon conseiller ne pourra pas me préconiser un investissement en adéquation à ma situation
                    </p>
                </div>
            ) : null}
        </div>
    );
};
SurveyForm.propTypes = {
    prefix: PropTypes.oneOf(['client', 'partner']).isRequired,
    decline: PropTypes.bool,
    getFieldName: PropTypes.func.isRequired,
    investorProfile: PropTypes.shape({
        path: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    }).isRequired,
    lifeInsuranceLosses: PropTypes.string,
    sufferTradingLosses: PropTypes.string,
    durableInvestment: PropTypes.string,
    enviromentalCriteria: PropTypes.string,
    socialCriteria: PropTypes.string,
    governanceCriteria: PropTypes.string,
    declinePartner: PropTypes.bool,
    initialValues: PropTypes.shape({
        civility: PropTypes.string,
        lastName: PropTypes.string,
        partnerCivility: PropTypes.string,
        partnerLastName: PropTypes.string,
    }),
    sufferTradingLossesPartner: PropTypes.string,
    lifeInsuranceLossesPartner: PropTypes.string,
    durableInvestmentPartner: PropTypes.string,
    enviromentalCriteriaPartner: PropTypes.string,
    socialCriteriaPartner: PropTypes.string,
    governanceCriteriaPartner: PropTypes.string,
};
SurveyForm.defaultProps = {
    decline: false,
    lifeInsuranceLosses: 'no',
    sufferTradingLosses: 'no',
    durableInvestment: 'no',
    enviromentalCriteria: 'no',
    socialCriteria: 'no',
    governanceCriteria: 'no',
};

const SurveySection = ({ civility, firstName, lastName, withTitle, prefix, extraProps }) => (
    <div className={withTitle && 'col-md-6'}>
        { withTitle && (
            <div className="section-title">
                <Title showIcon={false} ><b>Questionnaire de {civility} {firstName} {lastName}</b></Title>
            </div>)}
        <SurveyForm {...extraProps} prefix={prefix} />
    </div>
);
SurveySection.propTypes = {
    civility: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    withTitle: PropTypes.bool,
    prefix: PropTypes.oneOf(['client', 'partner']),
    dual: PropTypes.bool,
    extraProps: PropTypes.shape({}),
};

const SurveyInner = ({
    handleSubmit,
    params: { target },
    dispatch,
    dirty,
    ...props
}) => {
    const {
        decline,
        declinePartner,
        initialValues: {
            civility,
            firstName,
            lastName,
            partnerCivility,
            partnerFirstName,
            partnerLastName,
            signature,
            partnerSignature,
            universignTransaction,
        },
        investorProfile: { path: back, label },
    } = props;

    const declineSelected = 'client' === target ? decline : declinePartner;

    const hasExistingSignature = !!(signature || partnerSignature || universignTransaction);

    let nextButton;
    if ('client&partner' === target) {
        // Si simultané alors on accepte que l'un des deux n'ai pas répondu au questionnaire
        nextButton = (decline && declinePartner) ? label : 'Continuer';
    } else {
        nextButton = declineSelected ? label : 'Continuer';
    }

    const handleFormSubmit = (e) => {
        e.preventDefault();
        // Afficher la modale uniquement si le formulaire a été modifié ET qu'une signature existe
        if (dirty && hasExistingSignature) {
            dispatch(setModal(() => (
                <SignatureWarningModal
                    onCancel={() => dispatch(setModal(null))}
                    onConfirm={() => {
                        dispatch(setModal(null));
                        handleSubmit();
                    }}
                />
            )));
        } else {
            // Si aucune modification ou pas de signature existante, soumettre directement
            handleSubmit();
        }
    };

    return (
        <Form handleSubmit={handleFormSubmit}>
            {'client&partner' === target ? (
                <div className="row servey-container-dual">
                    <GridLayout>
                        <SurveySection civility={civility} firstName={firstName} lastName={lastName} prefix="client" extraProps={props} withTitle showIcon />
                        <SurveySection civility={partnerCivility} firstName={partnerFirstName} lastName={partnerLastName} prefix="partner" extraProps={props} withTitle showIcon />
                    </GridLayout>
                </div>
            ) : (
                <div className="container">
                    <SurveySection prefix={target} extraProps={props} />
                </div>
            )}

            <FormButtons list={null} back={back} nextButtonContent={nextButton} />

        </Form>
    );
};
SurveyInner.propTypes = {
    handleSubmit: PropTypes.func,
    dispatch: PropTypes.func,
    dirty: PropTypes.bool,
    params: PropTypes.shape({
        target: PropTypes.string,
    }),
    decline: PropTypes.bool,
    declinePartner: PropTypes.bool,
    initialValues: PropTypes.shape({
        civility: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        partnerCivility: PropTypes.string,
        partnerFirstName: PropTypes.string,
        partnerLastName: PropTypes.string,
        signature: PropTypes.string,
        partnerSignature: PropTypes.string,
        universignTransaction: PropTypes.string,
    }),
    investorProfile: PropTypes.shape({
        path: PropTypes.string,
        label: PropTypes.string,
    }),
};
SurveyInner.defaultProps = {
    params: {
        target: 'client',
    },
};

export default formProvider(SurveyInner);
